import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Printer } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Waybill() {
  const { id } = useParams();
  const { customers, productById } = useData();
  const [ship, setShip] = useState(null);
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/shipments/${id}`).then(async (r) => {
      setShip(r.data);
      const o = await api.get(`/orders/${r.data.order_id}`);
      setOrder(o.data);
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="p-10 text-[#EF4444]" data-testid="waybill-not-found">Sevkiyat bulunamadı.</div>;

  if (!ship) return <div className="p-10 text-[#6B7280]">Yükleniyor...</div>;
  const customer = customers.find((c) => c.id === ship.customer_id);

  return (
    <div className="min-h-screen bg-white text-black print:bg-white" data-testid="waybill-page">
      <div className="max-w-[800px] mx-auto p-8 sm:p-12">
        <div className="flex justify-end mb-6 print:hidden">
          <button onClick={() => window.print()} data-testid="waybill-print-btn"
            className="flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-5 h-11 rounded-md font-display uppercase tracking-wide">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>
        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
          <div>
            <Logo dark className="text-4xl" subtitle="Ayakkabı Üretim" testId="print-logo" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-4" data-testid="print-title">Sevk İrsaliyesi</h1>
          </div>
          <div className="text-right text-sm font-mono">
            <p>No: <b data-testid="waybill-no">{ship.id.slice(0, 8).toUpperCase()}</b></p>
            <p>Tarih: {new Date(ship.created_at).toLocaleDateString("tr-TR")}</p>
            {order && <p>Teslim: {order.delivery_date}</p>}
          </div>
        </div>
        <div className="mb-6 text-sm">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Alıcı</p>
          <p className="text-lg font-semibold" data-testid="waybill-customer">{ship.customer_name}</p>
          {customer?.contact_name && <p>Yetkili: {customer.contact_name}</p>}
          {customer?.phone && <p>Tel: {customer.phone}</p>}
          {customer?.cargo_company && <p data-testid="waybill-cargo">Kargo Firması: <b>{customer.cargo_company}</b></p>}
          {customer?.address && <p>{customer.address}</p>}
        </div>
        <WaybillKoliTable ship={ship} productById={productById} />
        {ship.note && <p className="text-sm mt-4">Not: {ship.note}</p>}
        <div className="grid grid-cols-2 gap-10 mt-16 text-sm text-center">
          <div className="border-t border-black pt-2">Teslim Eden</div>
          <div className="border-t border-black pt-2">Teslim Alan</div>
        </div>
      </div>
    </div>
  );
}

function WaybillKoliTable({ ship, productById }) {
  const counts = ship.koli_counts || {};
  const pids = [...new Set(ship.items.map((i) => i.product_id))];
  const byKoli = !!ship.koli_type_name;
  const pairs = (pid) => ship.items.filter((i) => i.product_id === pid).reduce((a, b) => a + b.qty, 0);
  const value = (pid) => (byKoli ? counts[pid] || 0 : pairs(pid));
  const grand = pids.reduce((a, pid) => a + value(pid), 0);
  const cell = "border border-black px-2 py-1.5";
  return (
    <div data-testid="waybill-koli-section">
      {ship.koli_type_name && <p className="text-sm mb-2"><b>Koli Tipi:</b> <span data-testid="waybill-koli-type">{ship.koli_type_name}</span></p>}
      <table className="w-full text-sm border border-black">
        <thead className="bg-gray-100">
          <tr className="text-left">
            <th className={cell}>#</th>
            <th className={cell}>Model Kodu</th>
            <th className={cell}>Renk</th>
            <th className={`${cell} text-right`}>{byKoli ? "Koli Adedi" : "Adet (Çift)"}</th>
          </tr>
        </thead>
        <tbody>
          {pids.map((pid, i) => {
            const p = productById(pid);
            return (
              <tr key={pid} data-testid={`waybill-koli-row-${pid}`}>
                <td className={cell}>{i + 1}</td>
                <td className={`${cell} font-mono`}>{p?.model_code || "—"}</td>
                <td className={cell}>{p?.model_color || "—"}</td>
                <td className={`${cell} text-right font-mono font-bold`}>{value(pid)}</td>
              </tr>
            );
          })}
          <tr className="font-bold">
            <td colSpan={3} className={`${cell} text-right`}>{byKoli ? "TOPLAM KOLİ" : "TOPLAM ÇİFT"}</td>
            <td className={`${cell} text-right font-mono`} data-testid="waybill-koli-total">{grand}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
